"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoutingSession = exports.RoutingMode = exports.RoutingRemoteContact = void 0;
const SortedContactList_1 = require("../contact/SortedContactList");
const utils_1 = require("@streamr/utils");
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const uuid_1 = require("uuid");
const RouterRpcRemote_1 = require("./RouterRpcRemote");
const DhtRpc_client_1 = require("../../../generated/packages/dht/protos/DhtRpc.client");
const Contact_1 = require("../contact/Contact");
const RecursiveOperationRpcRemote_1 = require("../recursive-operation/RecursiveOperationRpcRemote");
const getPreviousPeer_1 = require("./getPreviousPeer");
const identifiers_1 = require("../../identifiers");
const pull_1 = __importDefault(require("lodash/pull"));
const logger = new utils_1.Logger(module);
const MAX_FAILED_HOPS = 2;
const ROUTING_TABLE_MAX_SIZE = 5;
class RoutingRemoteContact extends Contact_1.Contact {
    routerRpcRemote;
    recursiveOperationRpcRemote;
    constructor(peer, localPeerDescriptor, rpcCommunicator) {
        super(peer);
        this.routerRpcRemote = new RouterRpcRemote_1.RouterRpcRemote(localPeerDescriptor, peer, rpcCommunicator, DhtRpc_client_1.RouterRpcClient, RouterRpcRemote_1.ROUTING_TIMEOUT);
        this.recursiveOperationRpcRemote = new RecursiveOperationRpcRemote_1.RecursiveOperationRpcRemote(localPeerDescriptor, peer, rpcCommunicator, DhtRpc_client_1.RecursiveOperationRpcClient, RouterRpcRemote_1.ROUTING_TIMEOUT);
    }
    getRouterRpcRemote() {
        return this.routerRpcRemote;
    }
    getRecursiveOperationRpcRemote() {
        return this.recursiveOperationRpcRemote;
    }
}
exports.RoutingRemoteContact = RoutingRemoteContact;
var RoutingMode;
(function (RoutingMode) {
    RoutingMode[RoutingMode["ROUTE"] = 0] = "ROUTE";
    RoutingMode[RoutingMode["FORWARD"] = 1] = "FORWARD";
    RoutingMode[RoutingMode["RECURSIVE"] = 2] = "RECURSIVE";
})(RoutingMode || (exports.RoutingMode = RoutingMode = {}));
class RoutingSession extends eventemitter3_1.default {
    sessionId = (0, uuid_1.v4)();
    ongoingRequests = new Set();
    contactedPeers = new Set();
    failedHopCounter = 0;
    successfulHopCounter = 0;
    stopped = false;
    options;
    constructor(options) {
        super();
        this.options = options;
    }
    onRequestFailed(nodeId) {
        logger.trace('onRequestFailed() sessionId: ' + this.sessionId);
        if (this.stopped) {
            return;
        }
        if (this.ongoingRequests.has(nodeId)) {
            this.ongoingRequests.delete(nodeId);
        }
        this.deleteParallelRootIfSource(nodeId);
        this.failedHopCounter += 1;
        if (this.failedHopCounter >= MAX_FAILED_HOPS) {
            logger.trace(`Stopping routing after ${MAX_FAILED_HOPS} failed attempts for sessionId: ${this.sessionId}`);
            this.emitFailure();
            return;
        }
        const contacts = this.updateAndGetRoutablePeers();
        if (contacts.length === 0 && this.ongoingRequests.size === 0) {
            logger.trace('routing failed, emitting routingFailed sessionId: ' + this.sessionId);
            this.emitFailure();
        }
        else {
            logger.trace('routing failed, retrying to route sessionId: ' + this.sessionId + ' failedHopCounter: ' + this.failedHopCounter);
            this.sendMoreRequests(contacts);
        }
    }
    emitFailure() {
        if (this.successfulHopCounter >= 1) {
            this.emit('partialSuccess');
        }
        else {
            this.emit('routingFailed');
        }
    }
    onRequestSucceeded() {
        logger.trace('onRequestSucceeded() sessionId: ' + this.sessionId);
        if (this.stopped) {
            return;
        }
        this.successfulHopCounter += 1;
        if (this.successfulHopCounter >= this.options.parallelism) {
            this.emit('routingSucceeded');
            return;
        }
        const contacts = this.updateAndGetRoutablePeers();
        if (contacts.length === 0) {
            this.emit('routingSucceeded');
        }
        else if (contacts.length > 0 && this.ongoingRequests.size === 0) {
            this.sendMoreRequests(contacts);
        }
    }
    async sendRouteMessageRequest(contact) {
        if (this.stopped) {
            return false;
        }
        const msg = {
            ...this.options.routedMessage,
            routingPath: this.options.routedMessage.routingPath.concat([this.options.localPeerDescriptor])
        };
        if (this.options.mode === RoutingMode.FORWARD) {
            return contact.getRouterRpcRemote().forwardMessage(msg);
        }
        else if (this.options.mode === RoutingMode.RECURSIVE) {
            return contact.getRecursiveOperationRpcRemote().routeRequest(msg);
        }
        else {
            return contact.getRouterRpcRemote().routeMessage(msg);
        }
    }
    updateAndGetRoutablePeers() {
        logger.trace('getRoutablePeers() sessionId: ' + this.sessionId);
        const previousPeer = (0, getPreviousPeer_1.getPreviousPeer)(this.options.routedMessage);
        const previousId = previousPeer ? (0, identifiers_1.toNodeId)(previousPeer) : undefined;
        const targetId = (0, identifiers_1.toDhtAddress)(this.options.routedMessage.target);
        let routingTable;
        if (this.options.routingTablesCache.has(targetId, previousId) && this.options.routingTablesCache.get(targetId, previousId).getSize() > 0) {
            routingTable = this.options.routingTablesCache.get(targetId, previousId);
        }
        else {
            routingTable = new SortedContactList_1.SortedContactList({
                referenceId: (0, identifiers_1.toDhtAddress)(this.options.routedMessage.target),
                maxSize: ROUTING_TABLE_MAX_SIZE,
                allowToContainReferenceId: true,
                nodeIdDistanceLimit: previousId
            });
            const contacts = this.options.getConnections()
                .map((peer) => new RoutingRemoteContact(peer, this.options.localPeerDescriptor, this.options.rpcCommunicator));
            routingTable.addContacts(contacts);
            this.options.routingTablesCache.set(targetId, routingTable, previousId);
        }
        return routingTable.getClosestContacts()
            .filter((contact) => !this.contactedPeers.has(contact.getNodeId()) && !this.options.excludedNodeIds.has(contact.getNodeId()));
    }
    sendMoreRequests(uncontacted) {
        logger.trace('sendMoreRequests() sessionId: ' + this.sessionId);
        if (this.stopped) {
            return;
        }
        if (uncontacted.length === 0) {
            this.emitFailure();
            return;
        }
        while ((this.ongoingRequests.size < this.options.parallelism) && (uncontacted.length > 0) && !this.stopped) {
            const nextPeer = uncontacted.shift();
            logger.trace(`Sending routeMessage request to contact: ${(0, identifiers_1.toNodeId)(nextPeer.getPeerDescriptor())} (sessionId=${this.sessionId})`);
            this.contactedPeers.add(nextPeer.getNodeId());
            this.ongoingRequests.add(nextPeer.getNodeId());
            this.addParallelRootIfSource(nextPeer.getNodeId());
            setImmediate(async () => {
                try {
                    const succeeded = await this.sendRouteMessageRequest(nextPeer);
                    if (succeeded) {
                        this.onRequestSucceeded();
                    }
                    else {
                        this.onRequestFailed(nextPeer.getNodeId());
                    }
                }
                catch (err) {
                    logger.debug('Unable to route message ', { err });
                }
                finally {
                    logger.trace('sendRouteMessageRequest returned');
                }
            });
        }
    }
    addParallelRootIfSource(nodeId) {
        if (this.options.mode === RoutingMode.RECURSIVE
            && (0, identifiers_1.areEqualPeerDescriptors)(this.options.localPeerDescriptor, this.options.routedMessage.sourcePeer)) {
            this.options.routedMessage.parallelRootNodeIds.push(nodeId);
        }
    }
    deleteParallelRootIfSource(nodeId) {
        if (this.options.mode === RoutingMode.RECURSIVE
            && (0, identifiers_1.areEqualPeerDescriptors)(this.options.localPeerDescriptor, this.options.routedMessage.sourcePeer)) {
            (0, pull_1.default)(this.options.routedMessage.parallelRootNodeIds, nodeId);
        }
    }
    stop() {
        this.stopped = true;
        this.emit('stopped');
        this.removeAllListeners();
    }
}
exports.RoutingSession = RoutingSession;
//# sourceMappingURL=RoutingSession.js.map